// tournament/tournamentNarratedHand.ts (TF1, closes TOURNAMENT-QA GAP-4) - the LIVE consumer of the
// ST-LIB baked tournament narration bundle (live/generatedTournamentNarration.ts, 1000 Haiku entries).
// Until now that bundle had NO live surface - it was proven only by unit test (tournamentLibrary.test.ts),
// so the tournament surface used inline templates and never called lookupNarration. This reuses the SAME
// enumeration that BAKED the bundle (`enumerateAxis` over `LOCAL_TOURNAMENT_AXES`) to get a card-free
// digest that is GUARANTEED in the bundle, then serves its baked prose via `lookupNarration` - a real,
// full narrated tournament hand at $0 (lookupNarration is a pure hash + lookup; ZERO /api requests).
import {
  enumerateAxis,
  LOCAL_TOURNAMENT_AXES,
  type GenBucket,
} from '../home/handLibrary/generator';
import { lookupNarration } from '../live/narrationLibrary';
import type { CoachText } from '@postflop/schema';

/** A narrated tournament hand served entirely offline from the ST-LIB library. */
export interface NarratedTournamentHand {
  /** the card-free bucketed spot (street, hand class, position, texture, ICM pressure, rec action). */
  bucket: GenBucket;
  /** the baked Haiku prose for this exact bucket (beginner + pro thinking stream, villain read). */
  coach: CoachText;
  /** the coachCacheKey the bundle was indexed by - shown as the $0-provenance proof in the UI. */
  key: string;
}

/**
 * Find a narrated tournament hand that resolves against the baked bundle, deterministically off `seed`.
 * Walks the local-tournament axes, enumerating buckets until one's digest hits the offline library. Pure
 * and $0 - `enumerateAxis` deals in-memory and `lookupNarration` is a static lookup; nothing touches the
 * network or the coach API. Returns null only if the bundle is somehow empty (a corpus bug, never a user
 * path), so the caller can fall back gracefully.
 */
export const narratedTournamentHand = (seed: string, seedsPerAxis = 60): NarratedTournamentHand | null => {
  for (const axis of LOCAL_TOURNAMENT_AXES) {
    const seeds = Array.from({ length: seedsPerAxis }, (_, i) => `${seed}::${axis.gameType}::${i}`);
    const { buckets } = enumerateAxis(axis, seeds);
    for (const bucket of buckets) {
      const coach = lookupNarration(bucket.digest);
      if (coach) return { bucket, coach, key: bucket.key };
    }
  }
  return null;
};
